import React, { useEffect, useRef, useState } from "react"
import { useMapStore } from "@common/features/GoogleMaps/mapStore"
import {
  calculateAndDisplayRoute,
  getUpdatedMapStyle,
} from "@lib/utils/google.maps"
import { useStore } from "@state/store"
import SearchBar from "./SearchBar"
import gsap from "gsap"
import useNetworkStatus from "@src/hooks/useNetworkStatus"

type MapCustomColors = {
  landscapeColor?: string
  waterColor?: string
  parksColor?: string
  labelsColor?: string
  localColor?: string
  arterialColor?: string
}

export type POI = {
  iconPath: any
  category?: string | null
  place: string
  description?: string
  size: number
  coordinates: { lat: number; lng: number }
} & {
  distances: {
    [key: string]: {
      distance: string
      duration: string
    }
  }
}

type MapProps = {
  map: google.maps.Map | null
  directionsRenderer: google.maps.DirectionsRenderer | null
  mapRef: React.RefObject<HTMLDivElement>
  panelRef?: React.RefObject<HTMLDivElement>
  categories: string[]
  mapSettings: {
    zoom: number
    center: { lat: number; lng: number }
    unitSystem?: "IMPERIAL" | "METRIC"
  }
  mapStyling: {
    styleKey?: string
    style?: any[]
    customColors?: MapCustomColors
  }
  mapPOIS: {
    mainMarker: POI
    pois: any
    poiSettings: {
      poiVisibility: "hideOthers" | "scaleActive"
      poiIcon: {
        url?: string
        publicURL?: string
      }
    }
  }
}

const calculateDistances = async (
  customPoi: POI,
  mainMarker: POI,
  unitSystem: string,
) => {
  const service = new google.maps.DistanceMatrixService()

  const travelModes = [
    google.maps.TravelMode.WALKING,
    google.maps.TravelMode.DRIVING,
    google.maps.TravelMode.BICYCLING,
    google.maps.TravelMode.TRANSIT,
  ]

  const distanceResults: any = {
    name: customPoi.place,
    coordinates: customPoi.coordinates,
    distances: {},
  }

  for (const mode of travelModes) {
    await new Promise<void>((resolve) => {
      service.getDistanceMatrix(
        {
          origins: [mainMarker.coordinates],
          destinations: [customPoi.coordinates],
          travelMode: mode,
          avoidHighways: true,
          unitSystem: google.maps.UnitSystem[unitSystem || "IMPERIAL"],
        },
        (response: any, status: any) => {
          if (status === "OK") {
            const element = response.rows[0].elements[0]
            distanceResults.distances[mode] = {
              distance: element?.distance.text,
              duration: element?.duration.text,
            }
          } else {
            console.error("Error with distance matrix service:", status)
          }
          resolve()
        },
      )
    })
  }

  // Return the distance results for the single POI
  return distanceResults
}

const Map = (props: MapProps) => {
  const { categories, map, directionsRenderer } = props
  const { mainMarker, pois } = props.mapPOIS
  const { unitSystem } = props.mapSettings
  const { poiVisibility } = props.mapPOIS?.poiSettings
  const contentControls = useStore((s) => s.contentControls)
  const currentCategory = categories[contentControls?.index]

  const {
    travelMode,
    transitMode,
    activePOI,
    mapInstance,
    placesState,
    setActivePOI,
    setRouteSteps,
  } = useMapStore((state) => ({
    placesState: state.placesState,
    travelMode: state.travelMode,
    transitMode: state.transitMode,
    activePOI: state.activePOI,
    mapInstance: state.mapInstance,
    setActivePOI: state.setActivePOI,
    setRouteSteps: state.setRouteSteps,
  }))

  const markersRef = useRef<{
    [key: string]: google.maps.marker.AdvancedMarkerElement
  }>({})

  const updatedMapStyle = getUpdatedMapStyle(
    props.mapStyling?.style,
    props.mapStyling?.customColors,
  )

  const currentPOIRef = useRef<any>(null)
  const opacityMarker = {
    value: 0.01,
  }

  useEffect(() => {
    const updateColors = async () => {
      if (map) {
        const styledMapType = new google.maps.StyledMapType(updatedMapStyle, {
          name: "Styled Map",
        })
        map.mapTypes.set("styled_map", styledMapType)
        map.setMapTypeId("styled_map")
      }
    }

    updateColors()
  }, [updatedMapStyle, map])

  useEffect(() => {
    const updateMarkers = async () => {
      if (map) {
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
          "marker",
        )) as google.maps.MarkerLibrary

        const filteredPOIs = pois.filter(
          (poi) =>
            currentCategory === "All" || poi.category === currentCategory,
        )

        // Create or update markers based on the filtered POIs
        filteredPOIs.forEach((poi: POI, i) => {
          const dist = placesState.find(
            (place: any) => place.name === poi.place,
          )
          poi.distances = dist?.distances || {}

          let marker = markersRef.current[poi.place]
          if (!marker) {
            const element = SVGPoiMarker(i, poi.place)

            marker = new AdvancedMarkerElement({
              map,
              title: poi.place,
              position: poi.coordinates,
              content: element,
              gmpClickable: true,
            })

            marker.addEventListener("gmp-click", () => {
              const poiOBJ = {
                name: poi.place,
                description: poi.description,
                category: poi.category || "",
                coordinates: poi.coordinates,
                distances: poi.distances,
              }

              if (currentPOIRef.current === null) {
                currentPOIRef.current = poiOBJ
              } else {
                if (currentPOIRef.current.name === poiOBJ.name) {
                  currentPOIRef.current = null
                } else {
                  currentPOIRef.current = poiOBJ
                }
              }
              setActivePOI(currentPOIRef.current)
            })

            markersRef.current[poi.place] = marker
          } else {
            // Update marker position if necessary
            if (marker.position !== poi.coordinates) {
              marker.position = poi.coordinates
            }
          }
        })

        // Remove markers not in the filteredPOIs
        Object.keys(markersRef.current).forEach((key) => {
          if (!filteredPOIs.find((poi) => poi.place === key)) {
            const marker = markersRef.current[key]
            marker.map = null
            delete markersRef.current[key]
          }
        })
      }
    }
    updateMarkers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCategory,
    map,
    pois,
    mainMarker.coordinates,
    setActivePOI,
    activePOI,
  ])

  useEffect(() => {
    if (map && directionsRenderer && activePOI) {
      const directionsService = new google.maps.DirectionsService()
      calculateAndDisplayRoute(
        activePOI.coordinates,
        mainMarker.coordinates,
        travelMode,
        transitMode,
        directionsService,
        directionsRenderer,
        mapInstance,
        setRouteSteps,
      )
    }
    if (activePOI === null && directionsRenderer && mapInstance) {
      directionsRenderer.setMap(mapInstance)
    }
    if (currentMarkerRef.current && !activePOI) {
      currentMarkerRef.current.map = null
    }
  }, [
    map,
    directionsRenderer,
    travelMode,
    transitMode,
    setRouteSteps,
    activePOI,
  ])

  const currentMarkerRef =
    useRef<google.maps.marker.AdvancedMarkerElement | null>(null)

  const setCustomSearchPOI = async (poi: any) => {
    let marker = null

    const customPOI = await calculateDistances(poi, mainMarker, unitSystem)

    setActivePOI(customPOI)

    if (map) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker",
      )) as google.maps.MarkerLibrary

      // Remove the old marker if it exists
      if (currentMarkerRef.current) {
        currentMarkerRef.current.map = null
      }

      const markerElement = document.createElement("img")
      markerElement.src = poi?.image
      markerElement.width = 50
      markerElement.height = 50
      markerElement.style.borderRadius = "50%"
      markerElement.style.border = "5px solid var(--accent)"
      markerElement.classList.add("marker")

      marker = new AdvancedMarkerElement({
        map,
        title: poi.place,
        position: poi.coordinates,
        content: markerElement,
      })

      // we need to store the marker in a ref so we can remove it
      // when we set a new custom search POI
      currentMarkerRef.current = marker
    }
  }

  const { connectionStatus } = useNetworkStatus()

  return (
    <>
      {map && connectionStatus === "online" && (
        <SearchBar
          mainMarker={mainMarker}
          onResultItemClick={setCustomSearchPOI}
        />
      )}
      <div id="map" ref={props.mapRef} />
    </>
  )
}

export default Map

function SVGPoiMarker(i, id) {
  const bgColor = getComputedStyle(document.body).getPropertyValue("--accent")
  const width = 65
  const margin = width / 5
  const svgNS = "http://www.w3.org/2000/svg"

  const mainMarkerElement = document.createElementNS(svgNS, "svg")
  mainMarkerElement.style.position = "absolute"
  mainMarkerElement.style.transform = "translateY(50%)"
  mainMarkerElement.setAttribute("width", "100%")
  mainMarkerElement.setAttribute("height", "100%")
  mainMarkerElement.setAttribute("viewBox", `0 0 ${width} ${width}`)
  mainMarkerElement.setAttribute("preserveAspectRatio", "xMidYMid meet")

  const rectangle = document.createElementNS(svgNS, "rect")
  rectangle.setAttribute("width", `${width - margin}`)
  rectangle.setAttribute("height", `${width - margin}`)
  rectangle.setAttribute("x", `${margin / 2}`)
  rectangle.setAttribute("y", `${margin / 2}`)
  rectangle.setAttribute("rx", `${50}`)
  rectangle.setAttribute("fill", bgColor)
  rectangle.setAttribute("data-map-poi", id)
  mainMarkerElement.appendChild(rectangle)

  const borderOne = document.createElementNS(svgNS, "polyline")
  borderOne.setAttribute("points", `1,${margin * 2} 1,1 ${margin * 2},1`)
  borderOne.setAttribute("stroke", "transparent")
  borderOne.setAttribute("stroke-width", `${margin * 0.1}`)
  borderOne.setAttribute("fill", "none")
  borderOne.setAttribute("data-map-poi", id)
  mainMarkerElement.appendChild(borderOne)

  const borderTwo = document.createElementNS(svgNS, "polyline")
  borderTwo.setAttribute(
    "points",
    `${width - margin * 2},${width - 1} ${width - 1},${width - 1} ${
      width - 1
    },${width - margin * 2}`,
  )
  borderTwo.setAttribute("stroke", "transparent")
  borderTwo.setAttribute("stroke-width", `${margin * 0.1}`)
  borderTwo.setAttribute("fill", "none")
  borderTwo.setAttribute("data-map-poi", id)
  mainMarkerElement.appendChild(borderTwo)

  const text = document.createElementNS(svgNS, "text")
  text.setAttribute("x", `${width / 2}`)
  text.setAttribute("y", `${width / 2}`)
  text.setAttribute("font-size", `${width * 0.3}`)
  text.setAttribute("text-anchor", "middle")
  text.setAttribute("dominant-baseline", "central")
  text.innerHTML = i + 1
  text.setAttribute("data-map-poi", id)
  text.setAttribute("font-family", "ThirdFont-Regular")
  mainMarkerElement.appendChild(text)

  const markerWrapper = document.createElement("div")
  markerWrapper.style.position = "relative"
  markerWrapper.style.width = `${width}px`
  markerWrapper.style.height = `${width}px`
  markerWrapper.style.position = "relative"
  markerWrapper.appendChild(mainMarkerElement)
  return markerWrapper
}
